import React, { useReducer, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Col, Container, Div, Icon, Input, Label, Row, Switch, Tag, Text } from '@pinpoint/design-kit';
import { connect } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';

import CONSTANTS from 'src/store/constants';
import { appActions, bcActions, usersEditActions } from 'src/store/actions';
import { Page, Rbac } from 'src/containers';
import { uuid, formatDate } from 'src/functions';
import { ClientsSelection, Message, RolesDropdown, SiteRolesDropdown, Button } from 'src/components';

const UserStatusDescription = {
  UNCONFIRMED: 'User has been created but not confirmed',
  CONFIRMED: 'User has been confirmed',
  ARCHIVED: 'User is no longer active',
  COMPROMISED: 'User is disabled due to a potential security threat',
  UNKNOWN: 'User status is not known',
  RESET_REQUIRED:
    'User is confirmed, but the user must request a code and reset their password before he or she can sign in',
  FORCE_CHANGE_PASSWORD:
    'The user is confirmed and the user can sign in using a temporary password, but on first sign-in, the user must change their password to a new value before doing anything else',
};

const HIDE_AGENCY = true;

const reducer = (state, action) => {
  switch (action.type) {
    case 'SET_MEMBERSHIPS': {
      return {
        ...state,
        memberships: action.payload.map((m) => ({ ...m, key: uuid() })),
      };
    }
    case 'REMOVE_MEMBERSHIP': {
      return {
        ...state,
        memberships: state.memberships.filter(({ key }) => key !== action.payload.key),
      };
    }
    case 'ADD_MEMBERSHIP': {
      return {
        ...state,
        memberships: [...state.memberships, { key: uuid(), client: {}, role: {} }],
      };
    }
    case 'UPDATE_CLIENT': {
      return {
        ...state,
        memberships: state.memberships.map((m) =>
          m.key === action.payload.key ? { ...m, client: action.payload.client } : m
        ),
      };
    }
    case 'UPDATE_ROLE': {
      return {
        ...state,
        memberships: state.memberships.map((m) =>
          m.key === action.payload.key ? { ...m, role: action.payload.role } : m
        ),
      };
    }
    case 'UPDATE_SITE_ROLE': {
      return {
        ...state,
        siteRole: action.payload,
      };
    }
    default:
      return state;
  }
};

const defaultValues = {
  username: '',
  email: '',
  givenName: '',
  familyName: '',
  address: '',
  team: '',
  isActive: false,
  lastLoginAt: '',
  siteRole: '',
};

function EditUser({
  history,
  match: {
    params: { userId: userIdParam },
  },
  clients,
  profile,
  fetchClientsStatus,
  user: fetchedUser,
  editUserStatus,
  editUserError,
  resendInvitationStatus,
  resendInvitationError,
  resetPasswordStatus,
  resetPasswordError,
  fetchUser,
  fetchUserStatus,
  editUser,
  resendInvitation,
  resetPassword,
  enqueueNotification,
  setUserEmail,
}) {
  const [state, dispatch] = useReducer(reducer, { memberships: [] });

  // Inititalize the form
  const { handleSubmit, errors, control, setValue, register: registerInput } = useForm({});

  const registerInputs = useCallback(() => {
    registerInput(
      { name: 'memberships', type: 'custom' },
      {
        validate: (memberships) =>
          fetchedUser.agency ||
          (!!memberships &&
            memberships.length > 0 &&
            memberships.every((m) => m.client && m.client.tenant && m.role && m.role.name)) ||
          'There are error(s) in the client membership',
      }
    );
    registerInput(
      { name: 'siteRole', type: 'custom' },
      {
        validate: (value) =>
          ['', null, undefined, CONSTANTS.ROLE.CLIENT_USER].includes(value?.name) ||
          ([CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES].includes(
            value?.name
          ) &&
            fetchedUser.email.match(/@pinpoint\.ai$/)) ||
          'User must have a Pinpoint email address',
      }
    );
  }, [fetchedUser.agency, fetchedUser.email, registerInput]);

  const onSubmit = useCallback(
    (data) => editUser(userIdParam, { ...data, memberships: state.memberships, siteRole: state.siteRole }),
    [editUser, state.memberships, state.siteRole, userIdParam]
  );

  function validateClient(client) {
    // so that clients assigned previously to users without checking for whitelist show up
    const membershipIds = fetchedUser.memberships.map((m) => m.client.id);
    if (membershipIds.includes(client.id)) return true;

    const whitelist = client.emailWhitelist;
    if (whitelist.includes('*')) return true;

    const whitelistCombined = whitelist.split(',').filter((elem) => ![null, undefined, ''].includes(elem));
    const emailDomain = fetchedUser.email.split('@')[1];
    return whitelistCombined.some((allowedDomain) => allowedDomain === emailDomain);
  }

  // Fetch the users when the page loads
  useEffect(() => {
    fetchUser(userIdParam);
  }, [fetchUser, userIdParam]);

  useEffect(() => {
    setValue('memberships', state.memberships, { shouldValidate: false });
  }, [state.memberships, setValue]);

  useEffect(() => {
    setValue('siteRole', state.siteRole, { shouldValidate: true });
  }, [state.siteRole, setValue]);
  // Set the email in the breadcrumb after the page loads
  useEffect(() => {
    if (fetchedUser && fetchedUser.username) {
      setUserEmail(fetchedUser.email || fetchedUser.username);
      // Set the memberships in the local reducer
      dispatch({ type: 'SET_MEMBERSHIPS', payload: fetchedUser.memberships });
      dispatch({ type: 'UPDATE_SITE_ROLE', payload: fetchedUser.siteRole });
      registerInputs();
    }
  }, [fetchedUser, registerInputs, setUserEmail]);

  // Check if we have data
  const hasData =
    fetchedUser.username &&
    clients.length > 0 &&
    fetchUserStatus === CONSTANTS.API.SUCCEEDED &&
    fetchClientsStatus === CONSTANTS.API.SUCCEEDED;

  // Get the user to be edited
  const user = { ...defaultValues, ...fetchedUser };

  // Check all the api status
  const apiStatuses = Array.of(fetchClientsStatus, fetchUserStatus);

  // Render notification when action is complete
  useEffect(() => {
    if (editUserStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(`Editing the user failed. Reason: ${editUserError}`);
    } else if (editUserStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('User edited successfully', { autoClose: true });
      setTimeout(() => {
        history.push('/users');
      }, 1000);
    }
  }, [editUserStatus, editUserError, enqueueNotification, history]);

  // Render notification when action is complete
  useEffect(() => {
    if (resendInvitationStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(`Resend Invitation Failed. Reason: ${resendInvitationError}`);
    } else if (resendInvitationStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Invitation resent successfully', { autoClose: true });
    }
  }, [resendInvitationStatus, resendInvitationError, enqueueNotification]);

  useEffect(() => {
    if (resetPasswordStatus === CONSTANTS.API.FAILED) {
      enqueueNotification(`Reset Password Failed. Reason: ${resetPasswordError}`);
    } else if (resetPasswordStatus === CONSTANTS.API.SUCCEEDED) {
      enqueueNotification('Password reset successfully', { autoClose: true });
    }
  }, [resetPasswordStatus, resetPasswordError, enqueueNotification]);

  return (
    <Container>
      {hasData ? (
        <>
          <Row p={{ t: '1.25rem' }}>
            <Col d="flex" size="6" gutterWidth="1.25rem">
              <Div>
                <Button
                  buttonStyle="primary"
                  onClick={() => {
                    history.push('/users');
                  }}
                  data-tip="go back"
                >
                  <Icon color="white" name="LongLeft" m={{ r: '0.5rem' }} size="1rem" />
                  BACK
                </Button>
              </Div>
            </Col>
            <Col d="flex" size="6" flexDir="row-reverse" gutterWidth="1.25rem">
              <Button
                buttonStyle="primary"
                onClick={() => {
                  resendInvitation(user.username);
                }}
                disabled={user.userStatus !== 'FORCE_CHANGE_PASSWORD'}
                data-tip="Resend invitation to a new user"
              >
                RESEND INVITATION
              </Button>
              <Button
                buttonStyle="primary"
                onClick={() => {
                  resetPassword(user.username);
                }}
                disabled={user.userStatus === 'FORCE_CHANGE_PASSWORD'}
                data-tip="Resets user's password and sends email"
              >
                RESET PASSWORD
              </Button>
            </Col>
          </Row>
          <Div bg="white" rounded="md" m={{ y: '1.25rem' }} p={{ x: '1.5rem', y: '1.25rem' }}>
            <Container>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                }}
              >
                <Row>
                  <Col size={{ xs: '12', md: '4' }} gutterWidth="1.25rem">
                    <Div d="flex" align="center" justify="flex-end" minH="2.5rem" p={{ t: '0.5rem' }}>
                      <Text textSize="subtitle" p={{ x: 0, y: '.75rem' }}>
                        Username
                      </Text>
                    </Div>
                  </Col>
                  <Col size={{ xs: '12', md: '8' }} gutterWidth="1.25rem">
                    <Text textSize="subtitle" textColor="blue" p={{ x: 0, t: '1.25rem' }}>
                      {user.username}
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                    <Text textColor="danger800" textSize="caption">
                      <ErrorMessage errors={errors} name="username" />
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col size={{ xs: '12', md: '4' }} gutterWidth="1.25rem">
                    <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                      <Text textSize="subtitle" p={{ x: 0, y: '.75rem' }}>
                        Email
                      </Text>
                    </Div>
                  </Col>
                  <Col size={{ xs: '12', md: '8' }} gutterWidth="1.25rem">
                    <Text textSize="subtitle" textColor="blue" p={{ x: 0, y: '.75rem' }}>
                      {user.email}
                    </Text>
                  </Col>
                </Row>
                <Row p={{ b: '1rem' }}>
                  <Col size={{ xs: '12', md: '4' }} gutterWidth="1.25rem">
                    <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                      <Text textSize="subtitle">Is Active</Text>
                    </Div>
                  </Col>
                  <Col size={{ xs: '12', md: '4' }} gutterWidth="1.25rem">
                    <Controller
                      control={control}
                      name="isActive"
                      defaultValue={user.isActive}
                      render={({ onChange, value }) => (
                        <Label
                          tag="div"
                          align="center"
                          cursor="pointer"
                          onClick={() => {
                            onChange(!value);
                          }}
                          p={{ t: '0.5rem' }}
                        >
                          <Text textSize="body">InActive&nbsp;&nbsp;&nbsp;</Text>
                          <Switch checked={value} activeColor="brand500" inactiveColor="gray500" activeShadow="0" />
                          <Text textSize="body">Active</Text>
                        </Label>
                      )}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                    <Text textColor="danger800" textSize="caption">
                      <ErrorMessage errors={errors} name="email" />
                    </Text>
                  </Col>
                </Row>

                <Rbac roles={[CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.CLIENT_SERVICES_ADMIN]}>
                  <Row p={{ b: '1rem' }}>
                    <Col size={{ xs: '12', md: '4' }} gutterWidth="1.25rem">
                      <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                        <Text textSize="subtitle">Site Role</Text>
                      </Div>
                    </Col>
                    <Col d="flex" align="center" size={{ xs: '12', md: '4' }} gutterWidth="1.25rem">
                      <SiteRolesDropdown
                        w="100%"
                        defaultValue={user.siteRole?.name || 'Please select a role'}
                        onSelectRole={(roleName) => {
                          const nextRole = { name: roleName };
                          dispatch({
                            type: 'UPDATE_SITE_ROLE',
                            payload: nextRole,
                          });
                        }}
                        currentRole={profile.siteRole || profile.role}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                      <Text textColor="danger800" textSize="caption">
                        <ErrorMessage errors={errors} name="siteRole" />
                      </Text>
                    </Col>
                  </Row>
                </Rbac>

                <Row p={{ b: '1rem' }}>
                  <Col size={{ xs: '12', md: '4' }} gutterWidth="1.25rem">
                    <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                      <Text textSize="subtitle">First Name</Text>
                    </Div>
                  </Col>
                  <Col d="flex" align="center" size={{ xs: '12', md: '8' }} gutterWidth="1.25rem">
                    <Controller
                      name="givenName"
                      as={Input}
                      control={control}
                      defaultValue={user.givenName}
                      rules={{
                        required: 'First name is required',
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                    <Text textColor="danger800" textSize="caption">
                      <ErrorMessage errors={errors} name="givenName" />
                    </Text>
                  </Col>
                </Row>
                <Row p={{ b: '1rem' }}>
                  <Col size={{ xs: '12', md: '4' }} gutterWidth="1.25rem">
                    <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                      <Text textSize="subtitle">Last Name</Text>
                    </Div>
                  </Col>
                  <Col d="flex" align="center" size={{ xs: '12', md: '8' }} gutterWidth="1.25rem">
                    <Controller
                      name="familyName"
                      as={Input}
                      control={control}
                      defaultValue={user.familyName}
                      rules={{
                        required: 'Family name is required',
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                    <Text textColor="danger800" textSize="caption">
                      <ErrorMessage errors={errors} name="familyName" />
                    </Text>
                  </Col>
                </Row>
                <Row p={{ b: '1rem' }}>
                  <Col size={{ xs: '12', md: '4' }} gutterWidth="1.25rem">
                    <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                      <Text textSize="subtitle">Team</Text>
                    </Div>
                  </Col>
                  <Col d="flex" align="center" size={{ xs: '12', md: '8' }} gutterWidth="1.25rem">
                    <Controller name="team" as={Input} control={control} defaultValue={user.team} rules={{}} />
                  </Col>
                </Row>
                <Row>
                  <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                    <Text textColor="danger800" textSize="caption">
                      <ErrorMessage errors={errors} name="team" />
                    </Text>
                  </Col>
                </Row>
                <Row p={{ b: '1rem' }}>
                  <Col size={{ xs: '12', md: '4' }} gutterWidth="1.25rem">
                    <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                      <Text textSize="subtitle">Address</Text>
                    </Div>
                  </Col>
                  <Col d="flex" align="center" size={{ xs: '12', md: '8' }} gutterWidth="1.25rem">
                    <Controller
                      name="address"
                      as={Input}
                      control={control}
                      defaultValue={user.address}
                      rules={{
                        required: 'address is required',
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                    <Text textColor="danger800" textSize="caption">
                      <ErrorMessage errors={errors} name="address" />
                    </Text>
                  </Col>
                </Row>
                {HIDE_AGENCY ? null : (
                  <Row p={{ b: '1rem' }}>
                    <Col size={{ xs: '12', md: '4' }} gutterWidth="1.25rem">
                      <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                        <Text textSize="subtitle">Type</Text>
                      </Div>
                    </Col>
                    <Col size={{ xs: '12', md: '4' }} gutterWidth="1.25rem">
                      <Label
                        tag="div"
                        align="center"
                        cursor="not-allowed"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <Text>Agency User&nbsp;&nbsp;&nbsp;</Text>
                        <Switch
                          checked={!user.agency}
                          activeColor="gray500"
                          inactiveColor="gray500"
                          disabled
                          activeShadow="0"
                        />
                        <Text>Customer</Text>
                      </Label>
                    </Col>
                  </Row>
                )}
                {user.agency ? (
                  <Row p={{ b: '1rem' }}>
                    <Col size={{ xs: '12', md: '4' }} gutterWidth="1.25rem">
                      <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                        <Text textSize="subtitle" p={{ y: '.75rem' }}>
                          Agency
                        </Text>
                      </Div>
                    </Col>
                    <Col size={{ xs: '12', md: '8' }} gutterWidth="1.25rem">
                      <Tag textSize="subtitle" textColor="blue" p={{ x: 0, y: '.75rem' }}>
                        {user.agency.name}
                      </Tag>
                    </Col>
                  </Row>
                ) : (
                  <Row p={{ b: '1rem' }}>
                    <Col size={{ xs: '12', md: '4' }} gutterWidth="1.25rem">
                      <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                        <Text textSize="subtitle">Clients</Text>
                      </Div>
                    </Col>
                    <Col size={{ xs: '12', md: '8' }} gutterWidth="1.25rem">
                      {state.memberships.map(({ client, role, key }) => (
                        <Row d="flex" align="center" p={{ b: '1rem' }}>
                          <Col size={{ xs: '12', md: '6' }} gutterWidth="1.25rem">
                            <ClientsSelection
                              w="100%"
                              loading={fetchClientsStatus === CONSTANTS.API.STARTED}
                              clients={clients.filter(validateClient)}
                              tenant={client && client.tenant}
                              defaultText="Please select a client"
                              onSelectClient={(nextClient) => {
                                dispatch({
                                  type: 'UPDATE_CLIENT',
                                  payload: {
                                    client: nextClient,
                                    key,
                                  },
                                });
                              }}
                            />
                          </Col>
                          <Col size={{ xs: '12', md: '5' }} gutterWidth="1.25rem">
                            <RolesDropdown
                              w="100%"
                              defaultValue={CONSTANTS.ROLE_RENAMED[role.name] || role.name || 'Please select a role'}
                              onSelectRole={(roleName) => {
                                const nextRole = { name: roleName };
                                dispatch({
                                  type: 'UPDATE_ROLE',
                                  payload: {
                                    role: nextRole,
                                    key,
                                  },
                                });
                              }}
                              currentRole={profile.role}
                            />
                          </Col>
                          <Col size={{ xs: '12', md: '1' }} gutterWidth="1.25rem">
                            <Button
                              buttonStyle="primary"
                              disabled={state.memberships.length === 1}
                              onClick={() => {
                                dispatch({
                                  type: 'REMOVE_MEMBERSHIP',
                                  payload: {
                                    key,
                                  },
                                });
                              }}
                            >
                              <Icon color="white" name="Delete" size="1rem" />
                            </Button>
                          </Col>
                        </Row>
                      ))}
                    </Col>
                  </Row>
                )}
                <Row p={{ b: '1rem' }}>
                  <Col offset={{ xs: '0', md: '4' }} size={{ xs: '12', md: '8' }}>
                    <Text textColor="danger800" textSize="caption">
                      <ErrorMessage errors={errors} name="memberships" />
                    </Text>
                  </Col>
                </Row>
                <Row>
                  <Col offset="4" size={{ xs: '12', md: '2' }} gutterWidth="1.25rem">
                    <Button
                      buttonStyle="primary"
                      onClick={() => {
                        dispatch({
                          type: 'ADD_MEMBERSHIP',
                        });
                      }}
                    >
                      <Icon name="Plus" color="white" m={{ r: '0.5rem' }} size="1rem" />
                      ADD MEMBERSHIP
                    </Button>
                  </Col>
                </Row>
                <Row p={{ t: '1rem' }}>
                  <Col size={{ xs: '12', md: '4' }} gutterWidth="1.25rem">
                    <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                      <Text textSize="subtitle" p={{ y: '.75rem' }}>
                        Status
                      </Text>
                    </Div>
                  </Col>
                  <Col size={{ xs: '12', md: '8' }} gutterWidth="1.25rem">
                    <Div d="flex" align="center" justify="flex-start" minH="2.5rem">
                      <Text textSize="subtitle" textColor="blue" p={{ x: 0, y: '.75rem' }}>
                        {UserStatusDescription[user.userStatus] || '-'}
                      </Text>
                    </Div>
                  </Col>
                </Row>
                <Row p={{ b: '1rem' }}>
                  <Col size={{ xs: '12', md: '4' }} gutterWidth="1.25rem">
                    <Div d="flex" align="center" justify="flex-end" minH="2.5rem">
                      <Text textSize="subtitle" p={{ y: '.75rem' }}>
                        Last Login
                      </Text>
                    </Div>
                  </Col>
                  <Col size={{ xs: '12', md: '8' }} gutterWidth="1.25rem">
                    <Div d="flex" align="center" justify="flex-start" minH="2.5rem">
                      <Text textSize="subtitle" textColor="blue" p={{ x: 0, y: '.75rem' }}>
                        {user.lastLoginAt ? formatDate(new Date(user.lastLoginAt), 'mmm dd yyyy h:MM TT') : '-'}
                      </Text>
                    </Div>
                  </Col>
                </Row>
                <Row d="flex" justify="flex-end" p={{ b: '1rem' }}>
                  <Col>
                    <Button buttonStyle="primary" m={{ r: '0rem' }} onClick={handleSubmit(onSubmit)}>
                      <Text color="gray400">SAVE</Text>
                      {editUserStatus === CONSTANTS.API.STARTED ? (
                        <Icon color="gray400" name="Loading" m={{ x: '0.5rem' }} />
                      ) : null}
                    </Button>
                  </Col>
                </Row>
              </form>
            </Container>
          </Div>
        </>
      ) : apiStatuses.includes(CONSTANTS.API.STARTED) ? (
        <Message />
      ) : apiStatuses.includes(CONSTANTS.API.FAILED) ? (
        <Message type="error" />
      ) : apiStatuses.every((status) => status === CONSTANTS.API.SUCCEEDED) ? (
        <Message type="info" text="No data" />
      ) : (
        <Message type="info" text="No data" />
      )}
    </Container>
  );
}

const mapStateToProps = ({ app, usersEdit }) => ({
  ...app,
  ...usersEdit,
});

const mapDispatchToProps = (dispatch) => ({
  setUserEmail: (...params) => dispatch(bcActions.setUserEmail(...params)),
  fetchClients: () => dispatch(appActions.fetchClients()),
  fetchUser: (...params) => dispatch(usersEditActions.fetchUser(...params)),
  editUser: (...params) => dispatch(usersEditActions.editUser(...params)),
  resendInvitation: (...params) => dispatch(usersEditActions.resendInvitation(...params)),
  resetPassword: (...params) => dispatch(usersEditActions.resetPassword(...params)),
  enqueueNotification: (...params) => dispatch(appActions.enqueueNotification(...params)),
});

EditUser.propTypes = {
  profile: PropTypes.shape({
    role: PropTypes.string,
    siteRole: PropTypes.string,
  }).isRequired,
  clients: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  user: PropTypes.shape({
    username: PropTypes.string,
    email: PropTypes.string,
    givenName: PropTypes.string,
    familyName: PropTypes.string,
    address: PropTypes.string,
    agency: PropTypes.shape({
      name: PropTypes.string,
    }),
    memberships: PropTypes.arrayOf(PropTypes.shape({})),
    team: PropTypes.string,
    isActive: PropTypes.bool,
    userStatus: PropTypes.string,
    lastLoginAt: PropTypes.string,
    siteRole: PropTypes.shape({}),
  }).isRequired,
  setUserEmail: PropTypes.func.isRequired,
  fetchClientsStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  fetchUserStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  editUserStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  editUserError: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  resendInvitationStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  resendInvitationError: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  resetPasswordStatus: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  resetPasswordError: PropTypes.oneOfType([PropTypes.string, undefined]).isRequired,
  fetchUser: PropTypes.func.isRequired,
  editUser: PropTypes.func.isRequired,
  resendInvitation: PropTypes.func.isRequired,
  resetPassword: PropTypes.func.isRequired,
  enqueueNotification: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Page(EditUser));
